import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, RefreshCw, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi } from "@/modules/collector_v2/hooks/useApi";
import { PageHeader } from "@/modules/collector_v2/components/common/PageHeader";
import { LoadingState } from "@/modules/collector_v2/components/common/LoadingState";
import { ErrorState } from "@/modules/collector_v2/components/common/ErrorState";
import { ProfileSummary } from "@/modules/collector_v2/components/profile/ProfileSummary";
import { HandHistoryTable } from "@/modules/collector_v2/components/profile/HandHistoryTable";
import { HandSummaryDrawer } from "@/modules/collector_v2/components/profile/HandSummaryDrawer";
import { apiPost, apiDelete, v2 } from "@/modules/collector_v2/lib/apiClient";
import { toast } from "sonner";

const PAGE_SIZE = 100;

export default function PlayerProfilePage() {
  const { uid } = useParams();
  const numericUid = Number(uid);

  // ---------- Profile (KPIs) ----------
  const profilePath = useMemo(() => v2.profile(numericUid), [numericUid]);
  const {
    data: profile,
    loading: profileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useApi(profilePath, [profilePath]);

  // ---------- History (last 100) ----------
  const historyPath = useMemo(
    () => v2.history(numericUid, { limit: PAGE_SIZE, skip: 0 }),
    [numericUid],
  );
  const {
    data: history,
    loading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useApi(historyPath, [historyPath]);

  // ---------- Collection ----------
  const collectionPath = useMemo(() => v2.collectionList(numericUid), [numericUid]);
  const {
    data: collection,
    loading: collectionLoading,
    error: collectionError,
    refetch: refetchCollection,
  } = useApi(collectionPath, [collectionPath]);

  // ---------- Show Culture (opcional: 404 si el jugador nunca mostró) ----------
  // No bloquea la página: si no existe perfil de show culture, simplemente no
  // se renderiza la tarjeta. Por eso ignoramos el error.
  const showCulturePath = useMemo(
    () => v2.showCultureProfile(numericUid),
    [numericUid],
  );
  const { data: showCultureData } = useApi(showCulturePath, [showCulturePath]);
  const showCulture = showCultureData?.profile || null;

  // ---------- Drawer state ----------
  const [drawerHandId, setDrawerHandId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [busyHandId, setBusyHandId] = useState(null);

  const openDrawer = useCallback((hand) => {
    setDrawerHandId(hand.hand_id);
    setDrawerOpen(true);
  }, []);

  // ---------- Toggle collect ----------
  const handleToggleCollect = useCallback(
    async (hand) => {
      if (!hand?.hand_id || !numericUid) return;
      const isCollected = !!hand.is_collected;
      const handId = hand.hand_id;
      setBusyHandId(handId);
      try {
        if (isCollected) {
          await apiDelete(v2.collectionItem(numericUid, handId));
          toast.success?.("Mano removida de la colección");
        } else {
          await apiPost(v2.collectionItem(numericUid, handId));
          toast.success?.("Mano agregada a la colección");
        }
        await Promise.all([refetchHistory(), refetchCollection(), refetchProfile()]);
      } catch (e) {
        toast.error?.(e?.response?.data?.detail || e.message || "Error al actualizar colección");
      } finally {
        setBusyHandId(null);
      }
    },
    [numericUid, refetchHistory, refetchCollection, refetchProfile],
  );

  const handleToggleFromDrawer = useCallback(
    async (handId, isCollected) => {
      const fakeHand = { hand_id: handId, is_collected: !!isCollected };
      await handleToggleCollect(fakeHand);
    },
    [handleToggleCollect],
  );

  const refreshAll = useCallback(() => {
    refetchProfile();
    refetchHistory();
    refetchCollection();
  }, [refetchProfile, refetchHistory, refetchCollection]);

  const collectedHandIds = useMemo(() => {
    const set = new Set();
    (collection?.items || []).forEach((c) => set.add(c.hand_id));
    (history?.items || []).forEach((h) => {
      if (h.is_collected) set.add(h.hand_id);
    });
    return set;
  }, [collection, history]);

  const drawerIsCollected = drawerHandId ? collectedHandIds.has(drawerHandId) : false;

  const titleNick =
    profile?.nick || (profileLoading ? "Cargando…" : `UID ${uid}`);

  return (
    <>
      <PageHeader
        eyebrow="Perfil de jugador"
        title={titleNick}
        description={
          profile
            ? `UID ${uid} · ${profile.hands_played ?? 0} manos · ${
                profile.tables_count ?? 0
              } mesas`
            : `Vista mirror del perfil PPPoker para UID ${uid}.`
        }
        action={
          <Button
            data-testid="player-profile-refresh-button"
            variant="outline"
            size="sm"
            onClick={refreshAll}
            disabled={profileLoading || historyLoading || collectionLoading}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${
                profileLoading || historyLoading || collectionLoading ? "animate-spin" : ""
              }`}
            />
            Refrescar
          </Button>
        }
      />

      {profileLoading && !profile && <LoadingState />}
      {profileError && !profile && (
        <ErrorState message={profileError} onRetry={refetchProfile} />
      )}

      {profile && (
        <Tabs defaultValue="resumen" className="w-full" data-testid="player-profile-tabs">
          <TabsList className="mb-4">
            <TabsTrigger value="resumen" data-testid="profile-tab-resumen">
              Resumen
            </TabsTrigger>
            <TabsTrigger value="historial" data-testid="profile-tab-historial">
              Historial ({profile.hands_played ?? 0})
            </TabsTrigger>
            <TabsTrigger value="coleccion" data-testid="profile-tab-coleccion">
              <Star className="h-3.5 w-3.5 mr-1.5" />
              Colección ({profile.collected_hands_count ?? 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="resumen" className="space-y-4">
            <ProfileSummary profile={profile} showCulture={showCulture} />
          </TabsContent>

          <TabsContent value="historial" className="space-y-4">
            {historyLoading && !history && (
              <Card>
                <CardContent className="py-12 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Cargando últimas {PAGE_SIZE} manos…
                </CardContent>
              </Card>
            )}
            {historyError && (
              <ErrorState message={historyError} onRetry={refetchHistory} />
            )}
            {history && (
              <Card>
                <CardContent className="pt-6">
                  <HandHistoryTable
                    items={history.items || []}
                    onToggleCollect={handleToggleCollect}
                    onOpenReplay={openDrawer}
                    busyHandId={busyHandId}
                    emptyLabel="Este jugador no tiene manos registradas todavía."
                  />
                  {history.count > history.items?.length && (
                    <p
                      className="text-xs text-muted-foreground text-center mt-3"
                      data-testid="history-pagination-hint"
                    >
                      Mostrando {history.items?.length || 0} de {history.count} manos.
                      Paginación completa disponible en versiones futuras.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="coleccion" className="space-y-4">
            {collectionLoading && !collection && (
              <Card>
                <CardContent className="py-12 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Cargando colección…
                </CardContent>
              </Card>
            )}
            {collectionError && (
              <ErrorState message={collectionError} onRetry={refetchCollection} />
            )}
            {collection && (
              <Card>
                <CardContent className="pt-6">
                  <HandHistoryTable
                    items={(collection.items || []).map((it) => ({
                      ...it,
                      is_collected: true,
                    }))}
                    onToggleCollect={handleToggleCollect}
                    onOpenReplay={openDrawer}
                    busyHandId={busyHandId}
                    emptyLabel="Aún no hay manos coleccionadas. Marcá una con la ⭐ desde Historial."
                  />
                  {collection.count > 0 && (
                    <p
                      className="text-xs text-muted-foreground text-center mt-3"
                      data-testid="collection-count-hint"
                    >
                      {collection.count} mano{collection.count === 1 ? "" : "s"} en
                      colección.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      <HandSummaryDrawer
        handId={drawerHandId}
        isCollected={drawerIsCollected}
        onToggleCollect={handleToggleFromDrawer}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </>
  );
}
